import { format } from "date-fns";
import {
  ExternalLink,
  FileText,
  Loader2,
  MailCheck,
  MailOpen,
  Package,
  PackagePlus,
  Pencil,
  Trash2,
} from "lucide-react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { NavigateFunction } from "react-router-dom";
import { LOG_TEMPLATES, LogKey } from "@/constants/log-templates.constants";
import { ExpeditionProcessStatusEnum } from "@/constants/enums/expeditionProcessStatus.enum";
import { DeliveryStatusEnum } from "@/constants/enums/deliveryStatus.enum";
import {
  deliveryStatusGroupToParams,
  DeliveryStatusGroup,
} from "@/components/backOffice/validatedExpeditions/useValidatedExpeditionsFilters.reducer";
import Paths from "@/constants/paths";
import { Button } from "@/components/ui/button";
import { SheetClose } from "@/components/ui/sheet";
import { useToggleNotificationReadBo } from "@/api/notifications.api";
import { useLogTemplateTexts } from "@/hooks/useLogTemplateTexts";

const ACTION_ICON_MAP: Array<{ match: string; icon: React.ElementType; color: string }> = [
  { match: "DELETE", icon: Trash2, color: "text-red-500" },
  { match: "UPDATE", icon: Pencil, color: "text-amber-500" },
  { match: "CREATE", icon: PackagePlus, color: "text-green-500" },
];

function NotificationIcon({ action }: { action: string }) {
  const { icon: Icon, color } = ACTION_ICON_MAP.find((e) => action.includes(e.match)) ?? {
    icon: Package,
    color: "text-gray-500",
  };
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100">
      <Icon className={`h-5 w-5 ${color}`} />
    </div>
  );
}

// Minimal common shape — compatible with both the BO drawer (GetNotificationsResponseDto)
// and the expedition modal (NotificationByExpeditionIdDto).
export type NotificationItemNotification = {
  id: string;
  createdAt: string | Date;
  log?: {
    action?: string;
    details?: unknown;
    createdAt?: string | Date;
    user?: { firstName?: string; lastName?: string } | null;
  } | null;
  expedition?: {
    quotation?: { identifier?: string } | null;
    customQuotation?: { identifier?: string } | null;
    company?: { businessName?: string };
    processStatus?: string;
    deliveryStatus?: string;
  } | null;
  readById?: string | null; // drawer: null = unread
  readAt?: string | null; // modal: null/undefined = unread
  readBy?: { firstName?: string; lastName?: string } | null;
};

// The finalized-expeditions-v2 page groups by `group`, not by raw delivery status (the
// old `tab` param is no longer read there). Built from deliveryStatusGroupToParams
// itself — the canonical status↔group mapping — instead of a hand-kept copy, so it
// can't silently drift out of sync with it or miss a status.
const GROUPS_WITH_DELIVERY_STATUS: DeliveryStatusGroup[] = [
  "ongoing",
  "delivered",
  "blocked",
  "failed",
];

const DELIVERY_STATUS_TO_GROUP: Record<string, DeliveryStatusGroup> =
  GROUPS_WITH_DELIVERY_STATUS.reduce(
    (acc, group) => {
      deliveryStatusGroupToParams(group).deliveryStatus?.forEach((status) => {
        acc[status] = group;
      });
      return acc;
    },
    {} as Record<string, DeliveryStatusGroup>,
  );

export const resolveDeliveryStatusGroup = (deliveryStatus?: string | null): DeliveryStatusGroup =>
  (deliveryStatus && DELIVERY_STATUS_TO_GROUP[deliveryStatus]) || "ongoing";

export const resolveNotificationTarget = (expedition: {
  quotation?: { identifier?: string } | null;
  customQuotation?: { identifier?: string } | null;
  processStatus?: string;
  deliveryStatus?: string;
}): { pathname: string; search: string } => {
  const { processStatus, deliveryStatus, quotation, customQuotation } = expedition;
  const identifier = quotation?.identifier
    ? `C-${quotation.identifier}`
    : customQuotation?.identifier
      ? `D-${customQuotation.identifier}`
      : "";
  const isQuotation = quotation !== null;

  if (processStatus === ExpeditionProcessStatusEnum.DELETED)
    return {
      pathname: Paths.bo.deletedExpeditionsV2,
      search: `search=${identifier}`,
    };
  if (
    !isQuotation &&
    (processStatus === ExpeditionProcessStatusEnum.PENDING_QUOTE ||
      processStatus === ExpeditionProcessStatusEnum.PENDING_QUOTE_ACCEPTATION)
  )
    return { pathname: Paths.bo.customQuotationsBox, search: `search=${identifier}` };
  if (processStatus === ExpeditionProcessStatusEnum.PENDING_PELICAN)
    return { pathname: Paths.bo.expeditionsBox, search: `search=${identifier}&week=false` };
  if (deliveryStatus === DeliveryStatusEnum.BLOCKED)
    return { pathname: Paths.bo.blockedExpeditionsV2, search: `search=${identifier}` };
  if (processStatus === ExpeditionProcessStatusEnum.VALIDATED) {
    return {
      pathname: Paths.bo.validatedExpeditionsV2,
      search: `search=${identifier}&group=${resolveDeliveryStatusGroup(deliveryStatus)}`,
    };
  }
  return { pathname: "", search: "" };
};

// Navigates to the expedition targeted by a notification. When we're already on that
// page, a SPA navigate() only changes the URL — the page's own filter state (local
// state/reducer initialized from the URL at mount) doesn't pick up the new params, so
// the click looks like it does nothing. A full reload guarantees the page reinitializes
// from the new URL.
export const goToNotificationTarget = (
  expedition: Parameters<typeof resolveNotificationTarget>[0],
  navigate: NavigateFunction,
) => {
  const { pathname, search } = resolveNotificationTarget(expedition);
  if (!pathname) return;
  const url = `${pathname}${search ? `?${search}` : ""}`;
  if (window.location.pathname === pathname) {
    window.location.href = url;
  } else {
    navigate({ pathname, search: search || "" });
  }
};

type NotificationItemProps = {
  notification: NotificationItemNotification;
  navigate?: NavigateFunction;
  onSelect?: () => void;
  isSelected?: boolean;
  showActions?: boolean;
  onDetail?: () => void;
  closeOnClick?: boolean;
};

export const NotificationItem = ({
  notification,
  navigate,
  onSelect,
  isSelected,
  showActions,
  onDetail,
  closeOnClick,
}: NotificationItemProps) => {
  const { t } = useTranslation("shared");
  const { getTitle } = useLogTemplateTexts();
  const { mutate: toggleRead, isPending: isTogglingRead } = useToggleNotificationReadBo();

  if (!notification?.expedition) return null;

  const action = notification.log?.action || "";
  const isUnread =
    notification.readById === null ||
    (notification.readById === undefined && notification.readAt == null);
  const isUrgent = LOG_TEMPLATES[action as LogKey]?.notification?.visibility.isUrgent === true;

  const identifierText = notification.expedition.quotation
    ? `C-${notification.expedition.quotation.identifier}`
    : notification.expedition.customQuotation
      ? `D-${notification.expedition.customQuotation.identifier}`
      : "";

  const handleClick = () => {
    if (onSelect) {
      onSelect();
      return;
    }
    if (!navigate || !notification.expedition) return;
    goToNotificationTarget(notification.expedition, navigate);
  };

  const handleNavigate = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!navigate || !notification.expedition) return;
    goToNotificationTarget(notification.expedition, navigate);
  };

  const rowButton = (
    <button
      className={clsx(
        "group flex w-full items-start gap-3 border-b px-4 py-3 text-left transition-colors hover:bg-gray-50",
        isSelected && "bg-blue-50 hover:bg-blue-50",
      )}
      onClick={handleClick}
      type="button"
    >
      <NotificationIcon action={action} />

      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-gray-900">{identifierText}</span>
          <div className="flex shrink-0 items-center gap-1.5">
            <span className="text-xs text-gray-400">
              {format(notification.createdAt, "dd/MM/yyyy – HH:mm")}
            </span>
            {isUnread && (
              <div
                className={`h-[10px] w-[10px] rounded-full ${isUrgent ? "bg-red-500" : "bg-blue-500"}`}
              />
            )}
          </div>
        </div>
        <p className="mt-0.5 text-sm text-gray-700">{getTitle(action)}</p>
        {notification.expedition.company?.businessName && (
          <p className="mt-0.5 text-sm text-gray-600/60">
            {notification.expedition.company.businessName}
          </p>
        )}

        {showActions && (
          <div className="mt-2 flex flex-wrap items-center justify-end gap-1.5 opacity-0 transition-opacity group-hover:opacity-100">
            {onDetail && (
              <Button
                type="button"
                size="icon"
                variant="outline"
                className="h-7 w-7"
                title={t("notificationItem.detail")}
                onClick={(e) => {
                  e.stopPropagation();
                  onDetail();
                }}
              >
                <FileText className="h-3.5 w-3.5" />
              </Button>
            )}
            {notification.expedition && (
              <SheetClose asChild>
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  className="h-7 w-7"
                  title={t("notificationItem.viewExpedition")}
                  onClick={handleNavigate}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              </SheetClose>
            )}
            <Button
              type="button"
              size="icon"
              variant="outline"
              className={`h-7 w-7 ${isUnread ? "border-green-300 text-green-600 hover:border-green-400 hover:bg-green-50 hover:text-green-700" : "border-gray-200 text-gray-400 hover:border-gray-300 hover:bg-gray-50"}`}
              title={
                isUnread ? t("notificationItem.markAsRead") : t("notificationItem.markAsUnread")
              }
              disabled={isTogglingRead}
              onClick={(e) => {
                e.stopPropagation();
                toggleRead({ id: notification.id, read: isUnread });
              }}
            >
              {isTogglingRead ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : isUnread ? (
                <MailCheck className="h-3.5 w-3.5" />
              ) : (
                <MailOpen className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
        )}
      </div>
    </button>
  );

  return closeOnClick ? <SheetClose asChild>{rowButton}</SheetClose> : rowButton;
};
